import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { FormControl, Autocomplete, TextField, CircularProgress } from '@mui/material';
import { ConfigEntryType } from 'config/config';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import { PartnerType } from 'pages/stakeholders/hooks/useStakeholdersQuery';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

type PartnerOptionType = { label: string; value: string };
type PartnersSearchResponseType = { contacts: PartnerType[] };

type PostPartnerProps = {
  field: ConfigEntryType;
};

function PostPartner({ field }: PostPartnerProps) {
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<PostFormData>();
  const partnerIds = (useWatch({ control })?.partnerIds || []) as string[];
  const { data: partnersData, isLoading: partnersLoading } =
    useSearchItemQuery<PartnersSearchResponseType>({
      searchType: 'stakeholders',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    });
  const { singularPartnersModuleName, pluralPartnersModuleName } = useBuildWiseModules();
  const [partnersOptions, selectedPartner] = useMemo(() => {
    let partner: { label: string; value: string } | undefined = undefined;

    if (partnerIds?.length) {
      partner = {
        label: partnerIds[0] as string,
        value: partnerIds[0] as string,
      };
    }

    const mappedPartners = (partnersData?.contacts || [])
      .filter((partnerData) => partnerData.name)
      .map((partnerData) => {
        if (partnerIds?.length && partnerData.id === partnerIds[0]) {
          partner = {
            label: partnerData.name,
            value: partnerData.id,
          };

          return partner;
        }

        return {
          label: partnerData.name,
          value: partnerData.id,
        };
      });

    return [mappedPartners, partner];
  }, [partnersData?.contacts, partnerIds]);
  const handleSelectPartner = (_: unknown, partner: PartnerOptionType | null) => {
    if (partner) {
      setValue('partnerIds', [partner.value], { shouldDirty: true, shouldValidate: true });
    } else {
      setValue('partnerIds', [], { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState }) => (
          <FormControl fullWidth>
            <Autocomplete
              onChange={handleSelectPartner}
              options={partnersOptions}
              autoHighlight
              value={selectedPartner}
              noOptionsText={
                partnersLoading ? 'Loading...' : `No ${pluralPartnersModuleName} Found`
              }
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Select ${singularPartnersModuleName}`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {partnersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                  error={!!fieldState.error}
                />
              )}
              disabled={isSubmitting}
            />
          </FormControl>
        )}
        name={field?.entryCode}
      />
    </>
  );
}

export default PostPartner;
